import { FormHelperText, InputLabel, OutlinedInput, OutlinedInputProps, Stack, StackProps } from "@mui/material";
import { ChangeEvent, FC, useEffect, useMemo } from "react";
import { DeleteField, GetFieldIsValidAndErrorMessage } from "../../../Redux/Reducers/redux-slice-form-validation";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import useNumberValidator, { NumberValidatorProps } from "../../../Utils/Hooks/Validators/useNumberValidator";

interface FormInputNumberProps extends Omit<OutlinedInputProps, "id" | "label" | "error" | "type"> {
  label?: string;
  validationProps: NumberValidatorProps;
  stackProps: StackProps;
}

const FormInputNumber: FC<FormInputNumberProps> = (props) => {
  const { label, validationProps, onChange, stackProps, ...otherProps } = props;

  const validateNumber = useNumberValidator(validationProps);
  const dispatch = useAppDispatch();

  const errorMessage = useAppSelector(GetFieldIsValidAndErrorMessage(validationProps.formId, validationProps.fieldId));
  const isValid = !errorMessage;

  const [isValidMemo, errorMessageMemo] = useMemo(() => {
    return [isValid, errorMessage];
  }, [isValid]);

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!props.value || event.target.value === "") {
      validateNumber(undefined);
      onChange && onChange(event);

      return;
    }

    let value = Number(event.target.value);

    if (Number.isNaN(value)) validateNumber(undefined);
    else validateNumber(value);

    onChange && onChange(event);
  };

  useEffect(() => {
    if (!props.value) validateNumber(undefined);

    let valueInt: number = parseInt(props.value?.toString() || "", 10);

    if (Number.isNaN(valueInt)) validateNumber(undefined);
    else validateNumber(valueInt);
  }, [validationProps.required]);

  useEffect(() => {
    return () => {
      dispatch(
        DeleteField({
          formId: validationProps.formId,
          formSectionId: validationProps.formSectionId,
          fieldId: validationProps.fieldId,
        })
      );
    };
  }, []);

  return (
    <Stack {...stackProps}>
      {label && (
        <InputLabel sx={{ mb: 1 }} htmlFor={`${validationProps.fieldId}-label`} required={validationProps.required}>
          {label}
        </InputLabel>
      )}
      <OutlinedInput
        id={validationProps.fieldId}
        onChange={handleOnChange}
        error={!isValidMemo}
        type="number"
        {...otherProps}
      />
      {!isValidMemo && <FormHelperText error>{errorMessageMemo}</FormHelperText>}
    </Stack>
  );
};

export default FormInputNumber;
